import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import T from 'prop-types';
import { createContext, forwardRef, useContext, useMemo } from 'react';
import styled from 'styled-components';
import { useId } from '../../hooks/useId';
import { purple, red } from '../../styles/colors';
import { createPropTypes, margin } from '../../styles/system';
import { formLabel } from '../../styles/typography';
import { unit } from '../../styles/unit';
import { Box } from '../Box/Box';
import { Label } from '../Label/Label';
import { Link } from '../Link/Link';
import { Text } from '../Text/Text';
import { Tooltip, TooltipIndicator } from '../Tooltip/Tooltip';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const Container = /*#__PURE__*/styled.div.withConfig({
  displayName: "Container",
  componentId: "sc-8r4qo7-0"
})(["display:inline-flex;flex-direction:column;max-width:100%;min-width:0;width:", ";flex-grow:", ";", ""], props => props.stretch ? '100%' : undefined, props => props.stretch ? 1 : undefined, margin.withDefaults({
  mb: 1
}));
const TopContainer = /*#__PURE__*/styled.div.withConfig({
  displayName: "TopContainer",
  componentId: "sc-8r4qo7-1"
})(["display:flex;align-items:baseline;min-height:", ";"], unit(6));
const BottomContainer = /*#__PURE__*/styled.div.withConfig({
  displayName: "BottomContainer",
  componentId: "sc-8r4qo7-2"
})(["display:flex;align-items:flex-start;min-height:", ";padding-top:", ";opacity:", ";"], unit(6), unit(1), props => props.disabled ? '0.48' : undefined);
const FormFieldAction = /*#__PURE__*/forwardRef(function FormFieldAction(props, forwardedRef) {
  return /*#__PURE__*/_jsx(Box, {
    flexGrow: "1",
    justifyContent: "flex-end",
    children: /*#__PURE__*/_jsx(Text, {
      variant: "caption",
      children: /*#__PURE__*/_jsx(Link, Object.assign({
        as: "button",
        type: "button",
        ref: forwardedRef
      }, props))
    })
  });
});
const FormFieldTooltipIndicator = /*#__PURE__*/styled(TooltipIndicator).withConfig({
  displayName: "FormFieldTooltipIndicator",
  componentId: "sc-8r4qo7-3"
})(["", " color:", ";align-self:flex-start;margin-left:", ";"], formLabel, purple, unit(1));
const FormFieldContext = /*#__PURE__*/createContext();

function useFormField() {
  return useContext(FormFieldContext);
}

const FormField = /*#__PURE__*/forwardRef(function FormField(_ref, forwardedRef) {
  let {
    id: controlledId,
    label,
    invalid = false,
    disabled = false,
    required = false,
    tooltipText,
    helperText,
    stretch = true,
    children,
    action
  } = _ref,
      restProps = _objectWithoutPropertiesLoose(_ref, ["id", "label", "invalid", "disabled", "required", "tooltipText", "tooltipIndicator", "helperText", "stretch", "children", "action"]);

  let id = useId('form-field-input');
  if (controlledId) id = controlledId;
  const tooltipId = `${id}-tooltip`;
  const ariaDescribedby = helperText ? `${id}-describedby` : undefined;
  const context = useMemo(() => ({
    id,
    'aria-describedby': ariaDescribedby,
    invalid,
    disabled,
    required
  }), [id, disabled, invalid, required, ariaDescribedby]);
  return /*#__PURE__*/_jsx(FormFieldContext.Provider, {
    value: context,
    children: /*#__PURE__*/_jsxs(Container, Object.assign({
      stretch: stretch,
      ref: forwardedRef
    }, restProps, {
      children: [/*#__PURE__*/_jsxs(TopContainer, {
        children: [/*#__PURE__*/_jsxs(Label, {
          htmlFor: id,
          disabled: disabled,
          children: [label, required && /*#__PURE__*/_jsx("span", {
            "aria-hidden": true,
            children: " *"
          })]
        }), tooltipText && /*#__PURE__*/_jsx(Tooltip, {
          id: tooltipId,
          title: tooltipText,
          placement: "top",
          children: /*#__PURE__*/_jsx(FormFieldTooltipIndicator, {})
        }), action]
      }), children, /*#__PURE__*/_jsx(BottomContainer, {
        disabled: disabled,
        children: helperText && /*#__PURE__*/_jsx(Text, {
          variant: "caption",
          textColor: invalid ? red : undefined,
          id: ariaDescribedby,
          children: helperText
        })
      })]
    }))
  });
});
FormField.propTypes = Object.assign({
  id: T.string,
  label: T.node,
  invalid: T.bool,
  disabled: T.bool,
  required: T.bool,
  stretch: T.bool,
  tooltipText: T.node,
  helperText: T.node,
  children: T.node
}, createPropTypes(margin.propNames));
export { FormField, FormFieldAction, useFormField };